import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ArrowLeft, Download, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { isCampaignEditable } from "@/api/perkpoint/campaigns";
import { useActiveProgramId } from "../../programs/program-scope";
import { ProgramsApi, programKeys } from "@/api/perkpoint/programs";
import { downloadCsvFile } from "../../report/reportDownloads";
import { campaigns as seedCampaigns } from "../Campaigns";
import { getSavedCampaignRows, programContext } from "../campaign-builder/model";
import { campaignPerformance, hasResults } from "./performance";

function n(value: number) {
  return Math.round(value).toLocaleString("en-IN");
}

function compact(value: number) {
  if (value >= 1e6) return `${(value / 1e6).toFixed(2).replace(/\.?0+$/, "")}M`;
  if (value >= 1e4) return `${(value / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return n(value);
}

function pct(part: number, whole: number, digits = 1) {
  return whole ? `${((part / whole) * 100).toFixed(digits)}%` : "—";
}

function Kpi({ label, value, meta, tone }: { label: string; value: string; meta?: string; tone?: "good" | "bad" }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900">{value}</p>
        {meta ? (
          <p className={cn("mt-1 text-xs", tone === "good" ? "text-green-700" : tone === "bad" ? "text-red-600" : "text-muted-foreground")}>
            {meta}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function CampaignPerformance() {
  const { id } = useParams();
  const { programId } = useActiveProgramId();
  const programQuery = useQuery({
    queryKey: programKeys.detail(programId ?? ""),
    queryFn: () => ProgramsApi.get(programId!),
    enabled: Boolean(programId),
  });
  const ctx = useMemo(() => programContext(programQuery.data), [programQuery.data]);
  const row = useMemo(
    () => [...getSavedCampaignRows(), ...seedCampaigns].find((item) => item.id === id),
    [id],
  );
  const perf = useMemo(() => (row ? campaignPerformance(row, ctx.costPerPoint) : null), [row, ctx.costPerPoint]);

  if (!row) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Campaign not found.</p>
        <Button asChild variant="outline"><Link to="/perkpoint/rewards/campaigns">Back to campaigns</Link></Button>
      </div>
    );
  }

  if (!perf || !hasResults(row.status)) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 py-10 text-center">
        <h1 className="text-2xl font-bold text-slate-900">{row.name}</h1>
        <p className="text-muted-foreground">
          This campaign is {row.status.toLowerCase()}. Results appear once it starts sending.
        </p>
        <Button asChild variant="outline"><Link to="/perkpoint/rewards/campaigns">Back to campaigns</Link></Button>
      </div>
    );
  }

  const money = (value: number) => `${ctx.currency} ${compact(value)}`;
  const funnel = [
    { label: "Sent", value: perf.sent },
    { label: "Delivered", value: perf.delivered },
    { label: "Opened or read", value: perf.opened },
    { label: "Clicked", value: perf.clicked },
    { label: "Converted", value: perf.converted },
  ];

  const exportCsv = () => {
    downloadCsvFile(`${row.code.toLowerCase()}-performance.csv`, {
      headers: ["Channel", "Sent", "Delivered", "Opened", "Clicked", "Converted", `Revenue (${ctx.currency})`],
      rows: [
        ...perf.byChannel.map((c) => [c.channel, c.sent, c.delivered, c.opened ?? "", c.clicked, c.converted, c.revenue]),
        ["Total", perf.sent, perf.delivered, perf.opened, perf.clicked, perf.converted, perf.revenue],
      ],
    });
    toast.success("Performance exported");
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-4">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2 h-8 px-2 text-slate-500">
            <Link to="/perkpoint/rewards/campaigns"><ArrowLeft className="mr-1 h-4 w-4" />Campaigns</Link>
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{row.name}</h1>
            <span className={cn("rounded-full px-2.5 py-1 text-xs font-medium",
              row.status === "Ongoing" ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-600")}>
              {row.status}
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">Sample data</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {row.code} · {row.type} · {row.channels.join(", ")} · {row.startDate} – {row.endDate}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="bg-white">
            <Link to={`/perkpoint/rewards/campaigns/${row.id}`}><Eye className="mr-1.5 h-4 w-4" />View setup</Link>
          </Button>
          {isCampaignEditable(row.apiStatus) ? (
            <Button asChild variant="outline" className="bg-white">
              <Link to={`/perkpoint/rewards/campaigns/${row.id}/edit`}><Pencil className="mr-1.5 h-4 w-4" />Edit</Link>
            </Button>
          ) : null}
          <Button type="button" variant="outline" className="bg-white" onClick={exportCsv}>
            <Download className="mr-1.5 h-4 w-4" />Export CSV
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        <Kpi label="Members reached" value={compact(perf.reached)} meta={`${compact(perf.sent)} messages sent`} />
        <Kpi label="Engagement" value={pct(perf.clicked, perf.delivered)} meta={`${compact(perf.clicked)} clicked · ${pct(perf.opened, perf.delivered, 0)} opened`} />
        <Kpi label="Conversions" value={compact(perf.converted)} meta={`${pct(perf.converted, perf.reached, 2)} of members reached`} />
        <Kpi label="Revenue" value={money(perf.revenue)} meta={`Avg order ${money(perf.avgOrderValue)}`} />
        <Kpi label="Points cost" value={money(perf.pointsCost)} meta={`${compact(perf.pointsIssued)} points issued`} />
        <Kpi label="Return on points" value={perf.roi ? `${perf.roi.toFixed(1)}x` : "—"}
          meta={perf.roi >= 1 ? "Revenue per unit of points cost" : "Revenue below points cost"}
          tone={perf.roi >= 1 ? "good" : "bad"} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <Card className="min-w-0 border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="mb-4 flex items-baseline justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-900">Conversions and revenue by day</h2>
              <span className="text-xs text-muted-foreground">Bars: conversions · Line: revenue</span>
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={perf.byDay} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} minTickGap={16} />
                  <YAxis yAxisId="c" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={40} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false}
                    width={56} tickFormatter={(v: number) => compact(v)} />
                  <Tooltip
                    formatter={(value: number, name: string) =>
                      name === "revenue" ? [money(value), "Revenue"] : [n(value), "Conversions"]}
                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                  />
                  <Bar yAxisId="c" dataKey="converted" fill="#86efac" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  <Line yAxisId="r" dataKey="revenue" stroke="#15803d" strokeWidth={2} dot={false} type="monotone" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="space-y-3 p-5">
            <h2 className="text-base font-semibold text-slate-900">Funnel</h2>
            {funnel.map((stage, i) => {
              const prev = i === 0 ? stage.value : funnel[i - 1].value;
              return (
                <div key={stage.label}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-slate-700">{stage.label}</span>
                    <span className="tabular-nums">
                      <span className="font-semibold text-slate-900">{n(stage.value)}</span>
                      {i > 0 ? <span className="ml-2 text-xs text-muted-foreground">{pct(stage.value, prev)}</span> : null}
                    </span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-slate-100">
                    <div className="h-2 rounded-full bg-green-600"
                      style={{ width: `${perf.sent ? Math.max(1, (stage.value / perf.sent) * 100) : 0}%` }} />
                  </div>
                </div>
              );
            })}
            <p className="pt-1 text-xs text-muted-foreground">SMS has no read tracking, so it is left out of "opened".</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <Card className="min-w-0 border-slate-200 shadow-sm">
          <CardContent className="space-y-4 p-5">
            <h2 className="text-base font-semibold text-slate-900">By channel</h2>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                    <TableHead>Channel</TableHead>
                    <TableHead className="text-right">Sent</TableHead>
                    <TableHead className="text-right">Delivered</TableHead>
                    <TableHead className="text-right">Opened</TableHead>
                    <TableHead className="text-right">Clicked</TableHead>
                    <TableHead className="text-right">Converted</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Cost / conversion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {perf.byChannel.map((c) => {
                    const share = perf.converted ? c.converted / perf.converted : 0;
                    return (
                      <TableRow key={c.channel}>
                        <TableCell className="font-medium text-slate-900">{c.channel}</TableCell>
                        <TableCell className="text-right tabular-nums">{n(c.sent)}</TableCell>
                        <TableCell className="text-right tabular-nums">{pct(c.delivered, c.sent)}</TableCell>
                        <TableCell className="text-right tabular-nums">{c.opened == null ? "—" : pct(c.opened, c.delivered)}</TableCell>
                        <TableCell className="text-right tabular-nums">{pct(c.clicked, c.delivered)}</TableCell>
                        <TableCell className="text-right tabular-nums">{n(c.converted)}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(c.revenue)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {c.converted ? money((perf.pointsCost * share) / c.converted) : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="space-y-4 p-5">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Against the control group</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {n(perf.controlSize)} members were held out and got nothing.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-green-50 p-3">
                <p className="text-xs text-green-800">Got the campaign</p>
                <p className="mt-1 text-xl font-bold text-green-900">{(perf.conversionRate * 100).toFixed(2)}%</p>
                <p className="text-xs text-green-800">converted</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="text-xs text-slate-600">Control group</p>
                <p className="mt-1 text-xl font-bold text-slate-900">{(perf.controlRate * 100).toFixed(2)}%</p>
                <p className="text-xs text-slate-600">{n(perf.controlConverted)} converted anyway</p>
              </div>
            </div>
            <div className="space-y-2 border-t border-slate-100 pt-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Lift</span>
                <span className={cn("font-semibold", perf.liftPts > 0 ? "text-green-700" : "text-red-600")}>
                  {perf.liftPts > 0 ? "+" : ""}{perf.liftPts.toFixed(2)} pts
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Revenue the campaign added</span>
                <span className="font-semibold text-slate-900">{money(perf.incrementalRevenue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Points cost per conversion</span>
                <span className="font-semibold text-slate-900">{money(perf.costPerConversion)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
